import React, { useMemo } from 'react';
import cx from 'classnames';

import styles from './NotificationContainer.module.scss';
import { NotificationContainerProps, NotificationPosition } from './types';
import { useNotification } from './NotificationContext';
import ToastNotification from '../../components/atoms/ToastNotification';

const ALL_POSITIONS: NotificationPosition[] = [
  'top-left',
  'top-center',
  'top-right',
  'bottom-left',
  'bottom-center',
  'bottom-right',
];

function getContainerStyle(
  position: NotificationPosition,
  offset: { x?: number; y?: number }
): React.CSSProperties {
  const style: React.CSSProperties = {};
  const x = offset.x ?? 20;
  const y = offset.y ?? 20;

  if (position.includes('top')) {
    style.top = `${y}px`;
  } else if (position.includes('bottom')) {
    style.bottom = `${y}px`;
    style.flexDirection = 'column-reverse';
  }

  if (position.includes('left')) {
    style.left = `${x}px`;
  } else if (position.includes('right')) {
    style.right = `${x}px`;
  } else if (position.includes('center')) {
    style.left = '50%';
    style.transform = 'translateX(-50%)';
  }

  return style;
}

const NotificationContainer: React.FC<NotificationContainerProps> = (props) => {
  const {
    className,
    position: defaultPosition = 'top-right',
    offset = { x: 20, y: 20 },
  } = props;
  const { notifications, removeNotification } = useNotification();

  const notificationsByPosition = useMemo(() => {
    const map = new Map<NotificationPosition, typeof notifications>();
    for (const n of notifications ?? []) {
      const pos = n.position ?? defaultPosition;
      if (!map.has(pos)) map.set(pos, []);
      map.get(pos)!.push(n);
    }
    return map;
  }, [notifications, defaultPosition]);

  return (
    <>
      {ALL_POSITIONS.map((pos) => {
        const toasts = notificationsByPosition.get(pos) ?? [];
        if (toasts.length === 0) return null;
        const containerStyle = getContainerStyle(pos, offset);
        const containerClass = cx(
          styles.notificationContainer,
          styles[pos],
          className
        );

        // State order is oldest → newest. Render newest first in DOM so top positions
        // stack down from the anchor (new at top, old at bottom) and bottom positions
        // with column-reverse stack up from the anchor (new at bottom, old toward center).
        const stackedToasts = [...toasts].reverse();

        return (
          <div key={pos} className={containerClass} style={containerStyle}>
            {stackedToasts.map((notification) => {
              const id = notification.id ?? 0;
              const close = () => removeNotification(id);
              const { message, renderContent, id: _omitId, position: _omitPosition, ...toastProps } = notification;
              return (
                <ToastNotification
                  key={id}
                  removeToast={close}
                  renderContent={
                    renderContent
                      ? () => renderContent({ closeToast: close })
                      : undefined
                  }
                  {...toastProps}
                >
                  {message}
                </ToastNotification>
              );
            })}
          </div>
        );
      })}
    </>
  );
};

export default NotificationContainer;
